import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Sparkles, MessageCircle, Home,
  User
} from 'lucide-react';
import SeoHead from '@components/SeoHead';
import Footer from '@components/Footer';

// --- Mock Components for the Phone Screen ---

const PhoneScreen1_Profile = () => (
  <div className="h-full flex flex-col p-6 bg-white">
    <div className="w-24 h-24 rounded-full bg-gray-100 mx-auto mb-6 overflow-hidden border-4 border-pink-50">
      <img src="https://i.pravatar.cc/150?img=32" alt="" className="w-full h-full object-cover" />
    </div>
    <div className="space-y-3">
      <div className="h-4 bg-gray-100 rounded w-3/4 mx-auto" />
      <div className="h-3 bg-gray-50 rounded w-1/2 mx-auto" />
    </div>
    <div className="mt-8 space-y-2">
      <div className="flex gap-2 flex-wrap justify-center">
        {['Night Owl', 'Clean Freak', 'Gamer', 'Non-Smoker'].map((tag) => (
          <span key={tag} className="px-3 py-1 bg-pink-50 text-pink-600 text-xs rounded-full font-medium">
            {tag}
          </span>
        ))}
      </div>
    </div>
    <div className="mt-auto bg-pink-500 text-white py-3 rounded-xl text-center font-bold text-sm shadow-lg shadow-pink-200">
      Profile 100% Complete
    </div>
  </div>
);

const PhoneScreen2_Matching = () => (
  <div className="h-full flex flex-col bg-gray-50 relative overflow-hidden">
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-64 h-64 border border-pink-200 rounded-full animate-ping opacity-20" />
      <div className="w-48 h-48 border border-pink-300 rounded-full animate-ping opacity-30 delay-75" />
    </div>
    <div className="relative z-10 flex-1 flex flex-col items-center justify-center p-6">
      <div className="w-full bg-white p-4 rounded-2xl shadow-xl mb-4 transform rotate-[-2deg]">
        <div className="flex items-center gap-3">
          <img src="https://i.pravatar.cc/150?img=12" alt="" className="w-12 h-12 rounded-full" />
          <div>
            <div className="font-bold text-gray-900">Sarah</div>
            <div className="text-xs text-green-500 font-bold">High Match</div>
          </div>
        </div>
      </div>
      <div className="w-full bg-white p-4 rounded-2xl shadow-xl transform rotate-[2deg] translate-x-2">
        <div className="flex items-center gap-3">
          <img src="https://i.pravatar.cc/150?img=5" alt="" className="w-12 h-12 rounded-full" />
          <div>
            <div className="font-bold text-gray-900">Mike</div>
            <div className="text-xs text-green-500 font-bold">Strong Match</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const PhoneScreen3_Chat = () => (
  <div className="h-full flex flex-col bg-white">
    <div className="p-4 border-b border-gray-100 flex items-center gap-3">
      <div className="w-8 h-8 rounded-full bg-pink-100 overflow-hidden">
        <img src="https://i.pravatar.cc/150?img=12" alt="" className="w-full h-full object-cover" />
      </div>
      <span className="font-bold text-sm">Sarah</span>
    </div>
    <div className="flex-1 p-4 space-y-4 overflow-hidden">
      <div className="flex justify-start">
        <div className="bg-gray-100 p-3 rounded-2xl rounded-tl-none text-xs text-gray-600 max-w-[80%]">
          Hey! I saw we both like hiking? 🏔️
        </div>
      </div>
      <div className="flex justify-end">
        <div className="bg-pink-500 text-white p-3 rounded-2xl rounded-tr-none text-xs max-w-[80%] shadow-md shadow-pink-200">
          Yes! I go to Lion&apos;s Head every weekend.
        </div>
      </div>
      <div className="flex justify-start">
        <div className="bg-gray-100 p-3 rounded-2xl rounded-tl-none text-xs text-gray-600 max-w-[80%]">
          That&apos;s awesome! We should team up for an apartment near there.
        </div>
      </div>
    </div>
    <div className="p-3 border-t border-gray-100">
      <div className="h-8 bg-gray-50 rounded-full w-full" />
    </div>
  </div>
);

const PhoneScreen4_Home = () => (
  <div className="h-full flex flex-col bg-gray-50 p-4">
    <div className="bg-white rounded-2xl overflow-hidden shadow-lg mb-4">
      <div className="h-32 bg-gray-200 relative">
        <img src="https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=400&q=80" alt="" className="w-full h-full object-cover" />
        <div className="absolute bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
          R 8,500 / mo
        </div>
      </div>
      <div className="p-3">
        <div className="font-bold text-sm mb-1">Sunny Loft in CBD</div>
        <div className="flex gap-2 text-xs text-gray-500">
          <span>2 Bed</span> • <span>2 Bath</span>
        </div>
      </div>
    </div>
    <div className="bg-white rounded-2xl overflow-hidden shadow-lg opacity-60">
      <div className="h-32 bg-gray-200 relative">
        <img src="https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=400&q=80" alt="" className="w-full h-full object-cover" />
      </div>
      <div className="p-3">
        <div className="h-4 bg-gray-100 rounded w-2/3 mb-2" />
      </div>
    </div>
  </div>
);

const steps = [
  {
    id: 0,
    title: 'Create Your Profile',
    description: "Tell us about your lifestyle, habits, and what you're looking for. Our AI uses 50+ data points to understand the real you.",
    icon: User,
    color: 'text-blue-500',
    bg: 'bg-blue-50',
    screen: PhoneScreen1_Profile,
  },
  {
    id: 1,
    title: 'Get Matched',
    description: 'Stop scrolling through endless lists. We curate a daily selection of compatible roommates who match your vibe and budget.',
    icon: Sparkles,
    color: 'text-purple-500',
    bg: 'bg-purple-50',
    screen: PhoneScreen2_Matching,
  },
  {
    id: 2,
    title: 'Connect Safely',
    description: 'Chat inside the app first. Get a feel for lifestyle fit before you share your personal contact info.',
    icon: MessageCircle,
    color: 'text-pink-500',
    bg: 'bg-pink-50',
    screen: PhoneScreen3_Chat,
  },
  {
    id: 3,
    title: 'Find a Home',
    description: "Once you've found your squad, browse listings that fit your combined budget. Apply together, move in together.",
    icon: Home,
    color: 'text-orange-500',
    bg: 'bg-orange-50',
    screen: PhoneScreen4_Home,
  },
] as const;

const PhoneFrame: React.FC<{ activeStep: number }> = ({ activeStep }) => {
  const ActiveScreen = steps[activeStep].screen;

  return (
    <div className="relative w-[280px] sm:w-[300px] lg:w-[320px] h-[560px] sm:h-[600px] lg:h-[640px] bg-gray-900 rounded-[3rem] shadow-2xl border-[8px] border-gray-900 overflow-hidden ring-1 ring-gray-900/5">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-gray-900 rounded-b-2xl z-20" />

      <div className="w-full h-full bg-white relative overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeStep}
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.97 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="absolute inset-0"
          >
            <ActiveScreen />
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="absolute inset-0 pointer-events-none rounded-[2.5rem] shadow-[inset_0_0_20px_rgba(0,0,0,0.35)] z-30" />

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 rounded-full bg-black/50 px-3 py-1.5 backdrop-blur-sm">
        {steps.map((step) => (
          <span
            key={step.id}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              step.id === activeStep ? 'w-5 bg-white' : 'w-1.5 bg-white/40'
            }`}
            aria-hidden="true"
          />
        ))}
      </div>
    </div>
  );
};

const HowItWorks: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const stepRefs = useRef<Array<HTMLElement | null>>([]);

  useEffect(() => {
    const nodes = stepRefs.current.filter(Boolean) as HTMLElement[];
    if (nodes.length === 0) return;

    const ratios = new Map<number, number>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = Number((entry.target as HTMLElement).dataset.stepIndex);
          if (Number.isNaN(index)) return;
          ratios.set(index, entry.isIntersecting ? entry.intersectionRatio : 0);
        });

        let bestIndex = 0;
        let bestRatio = -1;
        ratios.forEach((ratio, index) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestIndex = index;
          }
        });

        if (bestRatio > 0) {
          setActiveStep(bestIndex);
        }
      },
      {
        // Track the middle band of the viewport so the sticky phone updates
        // as each step becomes the focused section.
        root: null,
        rootMargin: '-35% 0px -35% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  const scrollToStep = (index: number) => {
    stepRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="min-h-screen bg-white font-sans selection:bg-pink-100 selection:text-pink-900">
      <SeoHead
        title="How RoomZa Works"
        description="See how RoomZa helps you find a compatible roommate in South Africa — create a profile, get AI-matched, chat safely, and discover a shared home together."
        path="/how-it-works"
      />

      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-gray-100" aria-label="Primary">
        <div className="max-w-7xl mx-auto px-6 h-16 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <img src="/logo.png" alt="RoomZa" className="w-8 h-8 rounded-lg" />
            <span className="font-bold text-gray-900">RoomZa</span>
          </Link>
          <Link
            to="/signup"
            className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
          >
            Get Started
          </Link>
        </div>
      </nav>

      <header className="pt-32 pb-16 px-6 text-center max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-50 text-pink-600 text-xs font-bold uppercase tracking-wider mb-6">
            <Sparkles className="w-3 h-3" />
            The RoomZa Journey
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 tracking-tight leading-tight">
            From stranger to <br />
            <span className="bg-gradient-to-r from-pink-500 to-rose-600 bg-clip-text text-transparent">
              perfect roommate.
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            We&apos;ve reimagined roommate matching in South Africa to put compatibility first. Here&apos;s how we help you find your people and your place.
          </p>
        </motion.div>
      </header>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {/* Desktop: two-column sticky phone layout */}
        <div className="lg:grid lg:grid-cols-2 lg:gap-12 lg:items-start">
          <div className="relative z-10 space-y-4 lg:space-y-0">
            {steps.map((step, index) => {
              const isActive = activeStep === index;
              return (
                <section
                  key={step.id}
                  ref={(el) => {
                    stepRefs.current[index] = el;
                  }}
                  data-step-index={index}
                  className="min-h-[70vh] lg:min-h-screen flex flex-col justify-center py-10 lg:py-6"
                >
                  <button
                    type="button"
                    onClick={() => scrollToStep(index)}
                    className={`text-left rounded-3xl p-6 transition-all duration-300 border ${
                      isActive
                        ? 'bg-white border-pink-200 shadow-xl shadow-pink-100/60 scale-[1.01]'
                        : 'bg-transparent border-transparent opacity-55 hover:opacity-80'
                    }`}
                  >
                    <div className={`w-14 h-14 ${step.bg} ${step.color} rounded-2xl flex items-center justify-center mb-6`}>
                      <step.icon className="w-7 h-7" />
                    </div>
                    <p className="text-xs font-bold uppercase tracking-widest text-pink-500 mb-2">
                      Step {index + 1}
                    </p>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                      {step.title}
                    </h2>
                    <p className="text-lg text-gray-600 leading-relaxed max-w-md">
                      {step.description}
                    </p>
                  </button>

                  {/* Mobile / tablet phone preview beside each step */}
                  <div className="lg:hidden mt-8 flex justify-center">
                    <PhoneFrame activeStep={index} />
                  </div>
                </section>
              );
            })}
          </div>

          {/* Sticky phone column — block layout (not flex) so position:sticky works */}
          <aside className="hidden lg:block relative" aria-hidden="true">
            <div className="sticky top-24 h-[calc(100vh-6rem)] flex items-center justify-center">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-tr from-pink-200 to-purple-200 rounded-full blur-[100px] opacity-50 pointer-events-none" />
              <PhoneFrame activeStep={activeStep} />
            </div>
          </aside>
        </div>
      </div>

      <div className="bg-gray-900 text-white py-24 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-8">Ready to find your perfect roommate?</h2>
          <p className="text-xl text-gray-400 mb-10">
            RoomZa is just getting started in South Africa. Create a free profile and help shape the community.
          </p>
          <Link
            to="/signup"
            className="bg-gradient-to-r from-pink-500 to-rose-600 text-white px-10 py-4 rounded-xl text-lg font-bold hover:scale-105 transition-transform shadow-lg shadow-pink-900/50 inline-flex items-center gap-2"
          >
            Get Started Now
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <Footer />
      </div>
    </div>
  );
};

export default HowItWorks;
